/**
 * Widget Data Bridge
 *
 * Generates the JavaScript code injected into every sandboxed AI-widget iframe.
 * Provides `window.GitfitData` — a high-level API that mirrors all the data
 * tools the AI backend has (goals, metrics, detailed entries, preferences, etc.)
 * so widgets can access user data dynamically at **runtime**, not just at
 * creation time.
 *
 * The generated code is plain ES5-compatible JS (no imports/modules) because it
 * runs inside a bare iframe with no build tooling.
 */

export function generateWidgetDataBridge(): string {
  // NOTE: This is a **string** of JavaScript that will be injected into the
  // iframe's <script> tag.  It must be self-contained and ES5-friendly.
  return `
// ═══════════════════════════════════════════════════════════════════════════════
// GitfitData — Runtime data bridge for AI-created widgets
// Gives every widget the same data access the AI backend tools have, live.
// ═══════════════════════════════════════════════════════════════════════════════

window.GitfitData = (function() {
  var _url   = window.__SUPABASE_URL__;
  var _key   = window.__SUPABASE_KEY__;
  var _token = window.__USER_TOKEN__;
  var _uid   = window.__USER_ID__;

  var _headers = {
    'apikey':        _key,
    'Authorization': 'Bearer ' + _token,
    'Content-Type':  'application/json'
  };

  // ── Internal helpers ──────────────────────────────────────────────────────

  function _fetch(path) {
    return fetch(_url + path, { headers: _headers }).then(function(r) {
      if (!r.ok) throw new Error('GitfitData fetch failed: ' + r.status);
      return r.json();
    });
  }

  // Simple per-session cache so repeated calls (e.g. goals) don't re-fetch.
  var _cache = {};

  // ── Date helpers ──────────────────────────────────────────────────────────

  function _pad(n) { return n < 10 ? '0' + n : '' + n; }

  function _fmt(d) {
    return d.getFullYear() + '-' + _pad(d.getMonth() + 1) + '-' + _pad(d.getDate());
  }

  // ── Public API ────────────────────────────────────────────────────────────

  return {

    // ─── Date Helpers ─────────────────────────────────────────────────────

    /** Today as YYYY-MM-DD */
    today: function() { return _fmt(new Date()); },

    /** N days ago as YYYY-MM-DD */
    daysAgo: function(n) {
      var d = new Date(); d.setDate(d.getDate() - n); return _fmt(d);
    },

    /** Start of the current ISO week (Monday) */
    weekStart: function() {
      var d = new Date();
      var day = d.getDay();
      d.setDate(d.getDate() - day + (day === 0 ? -6 : 1));
      return _fmt(d);
    },

    /** First day of the current month */
    monthStart: function() {
      var d = new Date();
      return d.getFullYear() + '-' + _pad(d.getMonth() + 1) + '-01';
    },

    // ─── Core Data Access ─────────────────────────────────────────────────

    /**
     * Get daily values for one metric over a date range.
     * @param {string} metricKey  e.g. 'calories', 'protein', 'custom_1_value'
     * @param {string} startDate  YYYY-MM-DD
     * @param {string} endDate    YYYY-MM-DD
     * @returns {Promise<Array<{date:string, value:number}>>}
     */
    getMetricValues: function(metricKey, startDate, endDate) {
      return _fetch(
        '/rest/v1/daily_entries?select=entry_date,' + metricKey
        + '&user_id=eq.' + _uid
        + '&entry_date=gte.' + startDate
        + '&entry_date=lte.' + endDate
        + '&order=entry_date.asc'
        + '&' + metricKey + '=not.is.null'
      ).then(function(rows) {
        return (rows || []).map(function(r) {
          return { date: r.entry_date, value: r[metricKey] };
        });
      });
    },

    /**
     * Get full daily_entries rows (all metric columns) for a date range.
     * @param {string} startDate  YYYY-MM-DD
     * @param {string} endDate    YYYY-MM-DD
     * @returns {Promise<Array<object>>}
     */
    getDailyEntries: function(startDate, endDate) {
      return _fetch(
        '/rest/v1/daily_entries?select=*'
        + '&user_id=eq.' + _uid
        + '&entry_date=gte.' + startDate
        + '&entry_date=lte.' + endDate
        + '&order=entry_date.asc'
      );
    },

    /**
     * Get detailed entry logs (individual food items, activities, etc.).
     * @param {string} startDate   YYYY-MM-DD
     * @param {string} [endDate]   YYYY-MM-DD (defaults to startDate)
     * @param {string} [metricKey] Optional filter by metric
     * @returns {Promise<Array<{entry_date, metric_key, entry_text, entry_time}>>}
     */
    getDetailedEntries: function(startDate, endDate, metricKey) {
      var path = '/rest/v1/detailed_entries?select=entry_date,metric_key,entry_text,entry_time'
        + '&user_id=eq.' + _uid
        + '&entry_date=gte.' + startDate
        + '&entry_date=lte.' + (endDate || startDate)
        + '&order=entry_date.asc,entry_time.asc'
        + '&limit=500';
      if (metricKey) path += '&metric_key=eq.' + metricKey;
      return _fetch(path);
    },

    /**
     * Get ALL user goals (cached after first call).
     * Returns the raw user_goals row with columns like calories_goal,
     * protein_goal, custom_1_goal, etc.
     * @returns {Promise<object>}
     */
    getGoals: function() {
      if (_cache.goals) return Promise.resolve(_cache.goals);
      return _fetch(
        '/rest/v1/user_goals?select=*&user_id=eq.' + _uid + '&limit=1'
      ).then(function(data) {
        var goals = data && data.length > 0 ? data[0] : {};
        _cache.goals = goals;
        return goals;
      });
    },

    /**
     * Get the goal value for a specific metric key.
     * @param {string} metricKey  e.g. 'calories', 'protein', 'custom_1'
     * @returns {Promise<number|null>}
     */
    getGoal: function(metricKey) {
      var goalMap = {
        calories: 'calories_goal',
        protein: 'protein_goal',
        carbs: 'carbs_goal',
        fiber: 'fiber_goal',
        added_sugars: 'added_sugars_goal',
        sleep: 'sleep_goal',
        water_gallons: 'water_goal',
        gym_time: 'gym_time_goal',
        miles_ran: 'miles_goal',
        body_weight: 'body_weight_goal',
        body_fat_percentage: 'body_fat_goal',
        github_commits: 'github_commits_goal'
      };
      return this.getGoals().then(function(goals) {
        // Standard metric
        if (goalMap[metricKey]) {
          var v = goals[goalMap[metricKey]];
          return v != null ? v : null;
        }
        // Custom metric  (custom_1_value → custom_1_goal)
        var slot = metricKey.replace('_value', '').match(/custom_(\\d+)/);
        if (slot) {
          var cv = goals['custom_' + slot[1] + '_goal'];
          return cv != null ? cv : null;
        }
        return null;
      });
    },

    /**
     * Get all ENABLED metrics with their names, units, types, and goals.
     * This is the runtime equivalent of the AI's get_user_metrics tool.
     * @returns {Promise<Array<{key, name, unit, type, goal, emoji}>>}
     */
    getUserMetrics: function() {
      if (_cache.userMetrics) return Promise.resolve(_cache.userMetrics);
      var self = this;
      return Promise.all([
        _fetch('/rest/v1/metric_preferences?select=*&user_id=eq.' + _uid + '&limit=1'),
        _fetch('/rest/v1/custom_metrics?select=*&user_id=eq.' + _uid),
        self.getGoals()
      ]).then(function(results) {
        var p = results[0] && results[0].length > 0 ? results[0][0] : {};
        var customs = results[1] || [];
        var goals   = results[2] || {};

        // Combine all enabled metric keys from the three category arrays
        var enabledKeys = {};
        var cats = [p.nutrition_metrics, p.activity_sleep_metrics, p.results_metrics];
        for (var ci = 0; ci < cats.length; ci++) {
          var arr = cats[ci] || [];
          for (var ai = 0; ai < arr.length; ai++) { enabledKeys[arr[ai]] = true; }
        }

        var stdMetrics = [
          { key:'calories',             name:'Calories',          unit:'kcal',    gk:'calories_goal',            tp:'numeric' },
          { key:'protein',              name:'Protein',           unit:'g',       gk:'protein_goal',             tp:'numeric' },
          { key:'carbs',                name:'Carbs',             unit:'g',       gk:'carbs_goal',               tp:'numeric' },
          { key:'fiber',                name:'Fiber',             unit:'g',       gk:'fiber_goal',               tp:'numeric' },
          { key:'added_sugars',         name:'Added Sugars',      unit:'g',       gk:'added_sugars_goal',        tp:'numeric' },
          { key:'sleep',                name:'Sleep',             unit:'hrs',     gk:'sleep_goal',               tp:'numeric' },
          { key:'water_gallons',        name:'Water',             unit:'gal',     gk:'water_goal',               tp:'numeric' },
          { key:'gym_time',             name:'Gym Time',          unit:'hrs',     gk:'gym_time_goal',            tp:'numeric' },
          { key:'miles_ran',            name:'Miles Ran',         unit:'mi',      gk:'miles_goal',               tp:'numeric' },
          { key:'body_weight',          name:'Body Weight',       unit:'lbs',     gk:'body_weight_goal',         tp:'numeric' },
          { key:'body_fat_percentage',  name:'Body Fat',          unit:'%',       gk:'body_fat_goal',            tp:'numeric' },
          { key:'mood',                 name:'Mood',              unit:'1-10',                                   tp:'rating',   rng:{min:1,max:10} },
          { key:'self_appearance_rating',name:'Appearance Rating', unit:'1-10',                                  tp:'rating',   rng:{min:1,max:10} },
          { key:'ate_healthy',          name:'Ate Healthy',       unit:'',                                       tp:'boolean',  rng:{min:0,max:1} },
          { key:'low_sugars',           name:'Low Sugars',        unit:'',                                       tp:'boolean',  rng:{min:0,max:1} },
          { key:'fasted',               name:'Fasted',            unit:'',                                       tp:'boolean',  rng:{min:0,max:1} },
          { key:'github_commits',       name:'GitHub Commits',    unit:'commits', gk:'github_commits_goal',      tp:'numeric' }
        ];

        var enabled = [];
        for (var i = 0; i < stdMetrics.length; i++) {
          var m = stdMetrics[i];
          if (enabledKeys[m.key]) {
            var entry = {
              key:  m.key,
              name: m.name,
              unit: m.unit,
              type: m.tp || 'numeric',
              goal: m.gk ? (goals[m.gk] != null ? goals[m.gk] : null) : null,
              emoji: null
            };
            if (m.rng) entry.range = m.rng;
            enabled.push(entry);
          }
        }

        // Custom metrics
        for (var j = 0; j < customs.length; j++) {
          var cm = customs[j];
          var slot = cm.metric_key.match(/custom_(\\d+)/);
          var slotNum = slot ? slot[1] : null;
          var gk = slotNum ? 'custom_' + slotNum + '_goal' : null;
          enabled.push({
            key:   cm.metric_key.indexOf('_value') !== -1 ? cm.metric_key : cm.metric_key + '_value',
            name:  cm.metric_name,
            unit:  cm.metric_unit,
            type:  cm.metric_type || 'number',
            goal:  gk ? (goals[gk] != null ? goals[gk] : null) : null,
            emoji: cm.metric_emoji || null
          });
        }

        _cache.userMetrics = enabled;
        return enabled;
      });
    },

    /**
     * Get metric preferences (which metrics are enabled).
     * @returns {Promise<object>}
     */
    getMetricPreferences: function() {
      if (_cache.prefs) return Promise.resolve(_cache.prefs);
      return _fetch(
        '/rest/v1/metric_preferences?select=*&user_id=eq.' + _uid + '&limit=1'
      ).then(function(data) {
        var prefs = data && data.length > 0 ? data[0] : {};
        _cache.prefs = prefs;
        return prefs;
      });
    },

    /**
     * Get custom metric definitions.
     * @returns {Promise<Array<{metric_key, metric_name, metric_unit, metric_type, metric_emoji}>>}
     */
    getCustomMetrics: function() {
      if (_cache.customMetrics) return Promise.resolve(_cache.customMetrics);
      return _fetch(
        '/rest/v1/custom_metrics?select=*&user_id=eq.' + _uid
      ).then(function(data) {
        _cache.customMetrics = data || [];
        return _cache.customMetrics;
      });
    },

    /**
     * Get dates the user intentionally marked as "no data" (rest days, off days).
     * These are DIFFERENT from days where the user simply forgot to log.
     * - Intentional no-data days should count as zero in averages and as logged rest days.
     * - Days with no entry AND not in no_data_entries are "missed logging" and should be excluded.
     * @param {string} startDate   YYYY-MM-DD
     * @param {string} endDate     YYYY-MM-DD
     * @param {string} [metricKey] Optional filter by metric key (without _value suffix)
     * @returns {Promise<Array<{entry_date:string, metric_key:string}>>}
     */
    getNoDataEntries: function(startDate, endDate, metricKey) {
      var path = '/rest/v1/no_data_entries?select=entry_date,metric_key'
        + '&user_id=eq.' + _uid
        + '&entry_date=gte.' + startDate
        + '&entry_date=lte.' + endDate
        + '&order=entry_date.asc'
        + '&limit=500';
      if (metricKey) path += '&metric_key=eq.' + metricKey;
      return _fetch(path);
    },

    /** Clear the session cache (call if underlying data may have changed). */
    clearCache: function() {
      for (var k in _cache) delete _cache[k];
    }
  };
})();
`;
}
